'use client'

import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, BookOpen, ChevronDown, Filter, RefreshCw, X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import z from 'zod/v4'

import type { BookDetails, RouterOutputs } from '@package/api'
import type { RenderLink } from '@ui/web/utils'
import {
  Button,
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Skeleton,
  SliderRange,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@ui/web/components'
import { ClassroomIcon, CollectionIcon, QuizIcon } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import { getStaticAssetUrl } from '@helper/utils/file'
import cn from '@config/tailwind/cn'

import type { CardAction } from '../components/cards/card-action'
import type { LessonModuleModalData } from '../components/lesson-module-modal'
import { BookDetailModal } from '../components/book-detail-modal'
import { BookCollectionCard, BookCoverCard, BookThemeCard, LessonModuleCard } from '../components/cards'
import { ContentSearch } from '../components/content-search'
import { LessonModuleModal } from '../components/lesson-module-modal'
import { useSharedTRPC } from '../context'

function mapToLessonModuleModalData(
  details: RouterOutputs['lesson']['getLessonModuleDetails'] & { type: 'lessonModule' },
): LessonModuleModalData {
  return {
    id: details.id,
    title: details.title,
    learningFocus: details.learningFocus,
    coverUrl: details.coverUrl,
    estimatedTimeMinutes: details.estimatedTimeMinutes,
    grade: details.grade,
    curricula: details.curricula,
    contributors: details.contributors,
    lessons: details.lessons,
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FormSchema = z.object({
  q: z.string().optional(),
  bookThemeId: z.array(z.string()).optional(),
  bookLevelId: z.array(z.string()).optional(),
  bookFormatId: z.array(z.string()).optional(),
  age: z.array(z.number()).optional(),
  curriculumId: z.array(z.string()).optional(),
  gradeId: z.array(z.string()).optional(),
  subjectId: z.array(z.string()).optional(),
  competencyId: z.array(z.string()).optional(),
  contributorId: z.array(z.string()).optional(),
  publisherId: z.array(z.string()).optional(),
})

export type UnifiedSearchValues = z.infer<typeof FormSchema>
export type UnifiedSearchChangeKey = keyof UnifiedSearchValues

type SearchTab = 'books' | 'collections' | 'practiceModules' | 'assignmentModules'
type BookItem = RouterOutputs['search']['searchBooks']['items'][number]
type CollectionItem = RouterOutputs['search']['searchBookCollections']['items'][number]
type ModuleItem = RouterOutputs['search']['searchLessonModules']['items'][number]

interface UnifiedSearchPageProps {
  initialValues?: UnifiedSearchValues
  initialTab?: SearchTab
  onTabChange?: (tab: SearchTab) => void
  renderBackLink: RenderLink
  renderBookCollectionLink: RenderLink
  renderBookModalAction: (book: BookDetails) => CardAction
  renderLessonModuleModalAction: (module: LessonModuleModalData) => CardAction
  renderCollectionLink?: RenderLink
  renderLoveBookLink?: RenderLink
  onBookTagClick?: (type: 'author' | 'publisher' | 'level' | 'theme', id: string) => void
  onSearchChange?: (values: UnifiedSearchValues, changedKeys: UnifiedSearchChangeKey[]) => void
  showLoveButton?: boolean
  isChild?: boolean
  className?: string
}

const TABS: { id: SearchTab; Icon: LucideIcon; heading: string }[] = [
  { id: 'books', Icon: BookOpen, heading: 'Buku' },
  { id: 'collections', Icon: CollectionIcon, heading: 'Seri & Kompilasi' },
  { id: 'practiceModules', Icon: QuizIcon, heading: 'Latihan dan Kuis' },
  { id: 'assignmentModules', Icon: ClassroomIcon, heading: 'Proyek dan Tugas' },
]

function normalizeValues(values: UnifiedSearchValues): UnifiedSearchValues {
  return {
    q: values.q?.trim() || undefined,
    bookThemeId: values.bookThemeId,
    bookLevelId: values.bookLevelId,
    bookFormatId: values.bookFormatId,
    age: values.age,
    curriculumId: values.curriculumId,
    gradeId: values.gradeId,
    subjectId: values.subjectId,
    competencyId: values.competencyId,
    contributorId: values.contributorId,
    publisherId: values.publisherId,
  }
}

interface ComboboxFilterOption {
  id: string
  name: string
  iconUrl?: string | null
}

interface ComboboxFilterProps {
  label: string
  placeholder?: string
  value: string[] | undefined
  options: ComboboxFilterOption[]
  onChange: (value: string[]) => void
}

function ComboboxFilter({
  label,
  placeholder = 'Pilih yang sesuai',
  value = [],
  options,
  onChange,
}: ComboboxFilterProps) {
  const selectedItems = options.filter((o) => value.includes(o.id))

  return (
    <label className='srd:flex srd:flex-col srd:gap-1'>
      <span className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-navy-100')}>{label}</span>
      <Combobox<ComboboxFilterOption, true>
        multiple
        value={selectedItems}
        onValueChange={(items) => onChange(items.map((i) => i.id))}
        items={options}
        isItemEqualToValue={(a, b) => a.id === b.id}
      >
        <ComboboxChips>
          {selectedItems.map((item) => (
            <ComboboxChip key={item.id}>
              {item.iconUrl ? (
                <img
                  src={item.iconUrl}
                  alt=''
                  className='srd:size-5 srd:shrink-0 srd:rounded-full srd:object-cover'
                  draggable={false}
                />
              ) : null}
              {item.name}
            </ComboboxChip>
          ))}
          <ComboboxChipsInput placeholder={placeholder} />
        </ComboboxChips>
        <ComboboxContent>
          <ComboboxEmpty>Tidak ada pilihan.</ComboboxEmpty>
          <ComboboxList>
            {(option: ComboboxFilterOption) => (
              <ComboboxItem key={option.id} value={option}>
                {option.iconUrl ? (
                  <img
                    src={option.iconUrl}
                    alt=''
                    className='srd:size-6 srd:shrink-0 srd:rounded-full srd:object-cover srd:mr-1'
                    draggable={false}
                  />
                ) : null}
                {option.name}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </label>
  )
}

function ResultSkeleton({ variant }: { variant: 'book' | 'bookCollection' | 'lessonModule' }) {
  return (
    <Skeleton
      className={cn(
        'srd:rounded-2xl srd:w-full srd:h-auto',
        variant === 'book' ? 'srd:aspect-3/4' : variant === 'bookCollection' ? 'srd:aspect-4/3' : 'srd:aspect-4/3',
      )}
    />
  )
}

function EmptyState() {
  return (
    <div className='srd:flex srd:min-h-[35dvh] srd:flex-col srd:items-center srd:justify-center srd:gap-y-6 srd:text-center'>
      <p className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-white')}>
        Woaaa... Hampa sekali...
      </p>
      <img src={getStaticAssetUrl('illustration/alien.png')} width={128} height={128} alt='' draggable={false} />
      <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-neutral-white')}>
        Coba gunakan filter atau kata kunci lainnya.
      </p>
    </div>
  )
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className='srd:flex srd:min-h-[35dvh] srd:flex-col srd:items-center srd:justify-center srd:gap-y-4 srd:text-center'>
      <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-white')}>
        Oops, Terjadi Kesalahan
      </p>
      <Button onClick={onRetry} className='srd:gap-2 srd:rounded-full'>
        <RefreshCw className='srd:size-4' />
        Coba Lagi
      </Button>
    </div>
  )
}

interface InfinitePager {
  pagination: { total: number; topRelevance?: number | null }
}

interface QueryPageSnapshot {
  isFetched: boolean
  data?: { pages?: InfinitePager[] }
}

function infiniteListFirstTotal(enabled: boolean, query: QueryPageSnapshot): number | undefined {
  if (!enabled || !query.isFetched) return undefined
  return query.data?.pages?.[0]?.pagination.total ?? 0
}

/** Best (lowest) title-match rank in this lane; undefined = lane off / not fetched / no keyword. */
function infiniteListTopRelevance(enabled: boolean, query: QueryPageSnapshot): number | undefined {
  if (!enabled || !query.isFetched) return undefined
  return query.data?.pages?.[0]?.pagination.topRelevance ?? undefined
}

function tabCountLabel(enabled: boolean, query: QueryPageSnapshot): string {
  if (!enabled) return '0'
  if (!query.isFetched) return '…'
  return String(query.data?.pages?.[0]?.pagination.total ?? 0)
}

export function UnifiedSearchPage({
  initialValues,
  initialTab,
  onTabChange,
  renderBackLink,
  renderBookCollectionLink,
  renderBookModalAction,
  renderLessonModuleModalAction,
  renderCollectionLink,
  renderLoveBookLink,
  onBookTagClick,
  onSearchChange,
  showLoveButton,
  isChild,
  className,
}: UnifiedSearchPageProps) {
  const trpc = useSharedTRPC()
  const queryClient = useQueryClient()
  const visibleTabs = isChild ? TABS.filter((t) => t.id !== 'assignmentModules') : TABS
  const hasInitializedSearchSyncRef = useRef(false)
  const previousDebouncedValuesRef = useRef<UnifiedSearchValues | null>(null)
  const [activeTab, setActiveTab] = useState<SearchTab>(initialTab ?? 'books')
  const [didPickTab, setDidPickTab] = useState(initialTab !== undefined)
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null)
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null)

  const form = useForm({
    defaultValues: {
      q: initialValues?.q ?? '',
      bookThemeId: initialValues?.bookThemeId,
      bookLevelId: initialValues?.bookLevelId,
      bookFormatId: initialValues?.bookFormatId,
      age: initialValues?.age,
      curriculumId: initialValues?.curriculumId,
      gradeId: initialValues?.gradeId,
      subjectId: initialValues?.subjectId,
      competencyId: initialValues?.competencyId,
      contributorId: initialValues?.contributorId,
      publisherId: initialValues?.publisherId,
    } as z.input<typeof FormSchema>,
  })

  const formValues = useStore(form.store, (state) => state.values)
  const currentQuery = formValues.q ?? ''
  const [debouncedQuery] = useDebouncedValue(currentQuery, { wait: 800 })
  const [debouncedBookThemeId] = useDebouncedValue(formValues.bookThemeId, { wait: 800 })
  const [debouncedBookLevelId] = useDebouncedValue(formValues.bookLevelId, { wait: 800 })
  const [debouncedBookFormatId] = useDebouncedValue(formValues.bookFormatId, { wait: 800 })
  const [debouncedAge] = useDebouncedValue(formValues.age, { wait: 800 })
  const [debouncedCurriculumId] = useDebouncedValue(formValues.curriculumId, { wait: 800 })
  const [debouncedGradeId] = useDebouncedValue(formValues.gradeId, { wait: 800 })
  const [debouncedSubjectId] = useDebouncedValue(formValues.subjectId, { wait: 800 })
  const [debouncedCompetencyId] = useDebouncedValue(formValues.competencyId, { wait: 800 })
  const [debouncedContributorId] = useDebouncedValue(formValues.contributorId, { wait: 800 })
  const [debouncedPublisherId] = useDebouncedValue(formValues.publisherId, { wait: 800 })

  const debouncedValues = useMemo(
    () =>
      normalizeValues({
        q: debouncedQuery,
        bookThemeId: debouncedBookThemeId,
        bookLevelId: debouncedBookLevelId,
        bookFormatId: debouncedBookFormatId,
        age: debouncedAge,
        curriculumId: debouncedCurriculumId,
        gradeId: debouncedGradeId,
        subjectId: debouncedSubjectId,
        competencyId: debouncedCompetencyId,
        contributorId: debouncedContributorId,
        publisherId: debouncedPublisherId,
      }),
    [
      debouncedQuery,
      debouncedBookThemeId,
      debouncedBookLevelId,
      debouncedBookFormatId,
      debouncedAge,
      debouncedCurriculumId,
      debouncedGradeId,
      debouncedSubjectId,
      debouncedCompetencyId,
      debouncedContributorId,
      debouncedPublisherId,
    ],
  )

  useEffect(() => {
    if (!hasInitializedSearchSyncRef.current) {
      hasInitializedSearchSyncRef.current = true
      previousDebouncedValuesRef.current = debouncedValues
      return
    }

    const previousValues = previousDebouncedValuesRef.current
    const changedKeys = (Object.keys(debouncedValues) as UnifiedSearchChangeKey[]).filter((key) => {
      const previousValue = previousValues?.[key]
      const nextValue = debouncedValues[key]

      if (Array.isArray(previousValue) && Array.isArray(nextValue)) {
        return previousValue.join(',') !== nextValue.join(',')
      }

      return previousValue !== nextValue
    })

    previousDebouncedValuesRef.current = debouncedValues

    if (changedKeys.length === 0) return
    onSearchChange?.(debouncedValues, changedKeys)
  }, [debouncedValues, onSearchChange])

  const { mutate: createUnifiedRecentSearch } = useMutation(
    trpc.search.createUnifiedRecentSearch.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.search.getUnifiedRecentSearches.queryKey() })
      },
    }),
  )

  const [
    bookFormatsQuery,
    bookLevelsQuery,
    bookThemesQuery,
    curriculaQuery,
    gradesQuery,
    subjectsQuery,
    competenciesQuery,
  ] = useQueries({
    queries: [
      { ...trpc.library.getAllBookFormats.queryOptions(), staleTime: Infinity },
      { ...trpc.library.getAllBookLevels.queryOptions(), staleTime: Infinity },
      { ...trpc.library.getAllBookThemes.queryOptions(), staleTime: Infinity },
      { ...trpc.lesson.getAllCurricula.queryOptions(), staleTime: Infinity },
      { ...trpc.lesson.getAllGrades.queryOptions(), staleTime: Infinity },
      { ...trpc.lesson.getAllSubjects.queryOptions(), staleTime: Infinity },
      { ...trpc.lesson.getAllCompetencies.queryOptions(), staleTime: Infinity },
    ],
  })

  const publisherIdsForLabels = formValues.publisherId ?? []
  const publisherLabelsQuery = useQuery({
    ...trpc.search.getPublisherLabelsByIds.queryOptions({ ids: publisherIdsForLabels }),
    enabled: publisherIdsForLabels.length > 0,
  })
  const publisherMetaById = useMemo(() => {
    const map = new Map<string, { name: string; logo: string | null }>()
    for (const row of publisherLabelsQuery.data ?? []) map.set(row.id, { name: row.name, logo: row.logo })
    return map
  }, [publisherLabelsQuery.data])

  const contributorIdsForLabels = formValues.contributorId ?? []
  const contributorLabelsQuery = useQuery({
    ...trpc.search.getContributorLabelsByIds.queryOptions({ ids: contributorIdsForLabels }),
    enabled: contributorIdsForLabels.length > 0,
  })
  const contributorMetaById = useMemo(() => {
    const map = new Map<string, { name: string; avatarUrl: string | null }>()
    for (const row of contributorLabelsQuery.data ?? []) map.set(row.id, { name: row.name, avatarUrl: row.avatarUrl })
    return map
  }, [contributorLabelsQuery.data])

  const isDefaultView =
    !debouncedValues.q &&
    !debouncedValues.bookThemeId?.length &&
    !debouncedValues.bookLevelId?.length &&
    !debouncedValues.bookFormatId?.length &&
    !debouncedValues.age?.length &&
    !debouncedValues.curriculumId?.length &&
    !debouncedValues.gradeId?.length &&
    !debouncedValues.subjectId?.length &&
    !debouncedValues.competencyId?.length &&
    !debouncedValues.contributorId?.length &&
    !debouncedValues.publisherId?.length

  const hasBookExclusiveFilter =
    (debouncedValues.bookThemeId?.length ?? 0) > 0 ||
    (debouncedValues.bookLevelId?.length ?? 0) > 0 ||
    (debouncedValues.bookFormatId?.length ?? 0) > 0 ||
    (debouncedValues.age?.length ?? 0) > 0 ||
    (debouncedValues.publisherId?.length ?? 0) > 0

  const hasLessonExclusiveFilter =
    (debouncedValues.curriculumId?.length ?? 0) > 0 ||
    (debouncedValues.gradeId?.length ?? 0) > 0 ||
    (debouncedValues.subjectId?.length ?? 0) > 0 ||
    (debouncedValues.competencyId?.length ?? 0) > 0

  /** Shared-only filters (`q`, `contributor`) keep every lane meaningful; facet filters disable irrelevant lanes only. */
  const booksQueryEnabled = !isDefaultView && !hasLessonExclusiveFilter
  const collectionsQueryEnabled = !isDefaultView && !hasBookExclusiveFilter && !hasLessonExclusiveFilter
  const lessonsQueryEnabled = !isDefaultView && !hasBookExclusiveFilter
  const hasSearchCriteria = !isDefaultView

  const booksQuery = useInfiniteQuery({
    ...trpc.search.searchBooks.infiniteQueryOptions(
      {
        q: debouncedValues.q,
        bookThemeId: debouncedValues.bookThemeId,
        bookLevelId: debouncedValues.bookLevelId,
        bookFormatId: debouncedValues.bookFormatId,
        contributorId: debouncedValues.contributorId,
        publisherId: debouncedValues.publisherId,
        age: debouncedValues.age as [number, number] | undefined,
        pageSize: 12,
      } as Parameters<typeof trpc.search.searchBooks.infiniteQueryOptions>[0],
      { getNextPageParam: (lastPage) => lastPage.nextCursor, initialCursor: 1 },
    ),
    enabled: booksQueryEnabled,
  })
  const collectionsQuery = useInfiniteQuery({
    ...trpc.search.searchBookCollections.infiniteQueryOptions(
      { q: debouncedValues.q, pageSize: 12 },
      { getNextPageParam: (lastPage) => lastPage.nextCursor, initialCursor: 1 },
    ),
    enabled: collectionsQueryEnabled,
  })
  const practiceModulesQuery = useInfiniteQuery({
    ...trpc.search.searchLessonModules.infiniteQueryOptions(
      {
        q: debouncedValues.q,
        curriculumId: debouncedValues.curriculumId,
        gradeId: debouncedValues.gradeId,
        subjectId: debouncedValues.subjectId,
        competencyId: debouncedValues.competencyId,
        contributorId: debouncedValues.contributorId,
        moduleKind: 'practice',
        pageSize: 12,
      },
      { getNextPageParam: (lastPage) => lastPage.nextCursor, initialCursor: 1 },
    ),
    enabled: lessonsQueryEnabled,
  })
  const assignmentModulesQuery = useInfiniteQuery({
    ...trpc.search.searchLessonModules.infiniteQueryOptions(
      {
        q: debouncedValues.q,
        curriculumId: debouncedValues.curriculumId,
        gradeId: debouncedValues.gradeId,
        subjectId: debouncedValues.subjectId,
        competencyId: debouncedValues.competencyId,
        contributorId: debouncedValues.contributorId,
        moduleKind: 'assignment',
        pageSize: 12,
      },
      { getNextPageParam: (lastPage) => lastPage.nextCursor, initialCursor: 1 },
    ),
    enabled: lessonsQueryEnabled && !isChild,
  })

  const {
    data: bookDetails,
    isLoading: bookDetailsLoading,
    isError: bookDetailsError,
  } = useQuery({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ...trpc.library.getBookDetails.queryOptions({ id: selectedBookId! }),
    enabled: !!selectedBookId,
  })

  const {
    data: moduleDetails,
    isLoading: moduleDetailsLoading,
    isError: moduleDetailsError,
  } = useQuery({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ...trpc.lesson.getLessonModuleDetails.queryOptions({ lessonModuleId: selectedModuleId! }),
    enabled: !!selectedModuleId,
  })

  const mappedModuleData =
    moduleDetails?.type === 'lessonModule' ? mapToLessonModuleModalData(moduleDetails) : undefined

  /** Undefined = lane off or waiting for fetch — excludes from naive `(0)` while loading */
  const counts = useMemo((): Record<SearchTab, number | undefined> => {
    if (!hasSearchCriteria) return { books: 0, collections: 0, practiceModules: 0, assignmentModules: 0 }
    const bq = booksQuery as QueryPageSnapshot
    const cq = collectionsQuery as QueryPageSnapshot
    const pq = practiceModulesQuery as QueryPageSnapshot
    const aq = assignmentModulesQuery as QueryPageSnapshot
    return {
      books: infiniteListFirstTotal(booksQueryEnabled, bq),
      collections: infiniteListFirstTotal(collectionsQueryEnabled, cq),
      practiceModules: infiniteListFirstTotal(lessonsQueryEnabled, pq),
      assignmentModules: infiniteListFirstTotal(lessonsQueryEnabled, aq),
    }
  }, [
    hasSearchCriteria,
    booksQueryEnabled,
    collectionsQueryEnabled,
    lessonsQueryEnabled,
    booksQuery.data,
    booksQuery.isFetched,
    collectionsQuery.data,
    collectionsQuery.isFetched,
    practiceModulesQuery.data,
    practiceModulesQuery.isFetched,
    assignmentModulesQuery.data,
    assignmentModulesQuery.isFetched,
  ])

  const { mutate: trackSearch } = useMutation(trpc.search.trackSearch.mutationOptions())
  const trackedSearchKeyRef = useRef<string | null>(null)

  /**
   * Emits exactly one `search_performed` per committed search.
   *
   * Waits until every lane that actually ran has reported a total, so
   * `resultCount` is the real total and not a partial one, then fires once and
   * remembers the signature — re-renders, tab switches and infinite-scroll
   * pages cannot re-fire it. The lanes used to each track their own event,
   * which put the metric at ~2x true search volume.
   */
  useEffect(() => {
    if (!hasSearchCriteria) {
      trackedSearchKeyRef.current = null
      return
    }

    const assignmentsEnabled = lessonsQueryEnabled && !isChild
    const activeCounts: (number | undefined)[] = []
    if (booksQueryEnabled) activeCounts.push(counts.books)
    if (collectionsQueryEnabled) activeCounts.push(counts.collections)
    if (lessonsQueryEnabled) activeCounts.push(counts.practiceModules)
    if (assignmentsEnabled) activeCounts.push(counts.assignmentModules)

    if (!activeCounts.length || activeCounts.some((count) => count === undefined)) return

    const searchKey = JSON.stringify(debouncedValues)
    if (trackedSearchKeyRef.current === searchKey) return
    trackedSearchKeyRef.current = searchKey

    const { q, ...filters } = debouncedValues

    trackSearch({
      q: q || undefined,
      resultCount: activeCounts.reduce<number>((sum, count) => sum + (count ?? 0), 0),
      resultCountByLane: {
        ...(booksQueryEnabled ? { books: counts.books } : {}),
        ...(collectionsQueryEnabled ? { collections: counts.collections } : {}),
        ...(lessonsQueryEnabled ? { practiceModules: counts.practiceModules } : {}),
        ...(assignmentsEnabled ? { assignmentModules: counts.assignmentModules } : {}),
      },
      filters,
    })
    // `trackSearch` is intentionally not a dependency: it is a stable mutate fn,
    // and keeping it out keys this effect to the search itself.
  }, [
    hasSearchCriteria,
    booksQueryEnabled,
    collectionsQueryEnabled,
    lessonsQueryEnabled,
    isChild,
    counts,
    debouncedValues,
  ])

  const tabBadgeText = useMemo((): Record<SearchTab, string> => {
    if (!hasSearchCriteria) return { books: '0', collections: '0', practiceModules: '0', assignmentModules: '0' }
    const bq = booksQuery as QueryPageSnapshot
    const cq = collectionsQuery as QueryPageSnapshot
    const pq = practiceModulesQuery as QueryPageSnapshot
    const aq = assignmentModulesQuery as QueryPageSnapshot
    return {
      books: tabCountLabel(booksQueryEnabled, bq),
      collections: tabCountLabel(collectionsQueryEnabled, cq),
      practiceModules: tabCountLabel(lessonsQueryEnabled, pq),
      assignmentModules: tabCountLabel(lessonsQueryEnabled, aq),
    }
  }, [
    hasSearchCriteria,
    booksQueryEnabled,
    collectionsQueryEnabled,
    lessonsQueryEnabled,
    booksQuery.data,
    booksQuery.isFetched,
    collectionsQuery.data,
    collectionsQuery.isFetched,
    practiceModulesQuery.data,
    practiceModulesQuery.isFetched,
    assignmentModulesQuery.data,
    assignmentModulesQuery.isFetched,
  ])

  /** Undefined = lane off / waiting / no keyword. Lower = better title match. */
  const relevances = useMemo((): Record<SearchTab, number | undefined> => {
    if (!debouncedValues.q)
      return { books: undefined, collections: undefined, practiceModules: undefined, assignmentModules: undefined }
    const bq = booksQuery as QueryPageSnapshot
    const cq = collectionsQuery as QueryPageSnapshot
    const pq = practiceModulesQuery as QueryPageSnapshot
    const aq = assignmentModulesQuery as QueryPageSnapshot
    return {
      books: infiniteListTopRelevance(booksQueryEnabled, bq),
      collections: infiniteListTopRelevance(collectionsQueryEnabled, cq),
      practiceModules: infiniteListTopRelevance(lessonsQueryEnabled, pq),
      assignmentModules: infiniteListTopRelevance(lessonsQueryEnabled, aq),
    }
  }, [
    debouncedValues.q,
    booksQueryEnabled,
    collectionsQueryEnabled,
    lessonsQueryEnabled,
    booksQuery.data,
    booksQuery.isFetched,
    collectionsQuery.data,
    collectionsQuery.isFetched,
    practiceModulesQuery.data,
    practiceModulesQuery.isFetched,
    assignmentModulesQuery.data,
    assignmentModulesQuery.isFetched,
  ])

  useEffect(() => {
    if (didPickTab) return
    if (!hasSearchCriteria) return

    const waitBooks = booksQueryEnabled && !booksQuery.isFetched
    const waitCollections = collectionsQueryEnabled && !collectionsQuery.isFetched
    const waitLessons = lessonsQueryEnabled && !(practiceModulesQuery.isFetched && assignmentModulesQuery.isFetched)
    if (waitBooks || waitCollections || waitLessons) return

    const hasKeyword = !!debouncedValues.q
    let best: SearchTab | null = null
    let bestRelevance = Infinity
    let bestCount = -1
    for (const tab of visibleTabs) {
      const n = counts[tab.id]
      if (n === undefined || n <= 0) continue
      // Keyword search: pick the lane holding the best title match, tiebreak by count.
      // No keyword (filter-only): pick the lane with the most results.
      const relevance = hasKeyword ? (relevances[tab.id] ?? Infinity) : Infinity
      const isBetter = hasKeyword
        ? relevance < bestRelevance || (relevance === bestRelevance && n > bestCount)
        : n > bestCount
      if (isBetter) {
        bestRelevance = relevance
        bestCount = n
        best = tab.id
      }
    }
    if (best !== null) setActiveTab(best)
  }, [
    counts,
    relevances,
    debouncedValues.q,
    didPickTab,
    hasSearchCriteria,
    booksQueryEnabled,
    collectionsQueryEnabled,
    lessonsQueryEnabled,
    booksQuery.isFetched,
    collectionsQuery.isFetched,
    practiceModulesQuery.isFetched,
    assignmentModulesQuery.isFetched,
  ])

  const handleBookTagClick = (type: 'author' | 'publisher' | 'level' | 'theme', id: string) => {
    if (type === 'author') form.setFieldValue('contributorId', [id])
    else if (type === 'publisher') form.setFieldValue('publisherId', [id])
    else if (type === 'level') form.setFieldValue('bookLevelId', [id])
    else if (type === 'theme') form.setFieldValue('bookThemeId', [id])
    setSelectedBookId(null)
    setActiveTab('books')
    setDidPickTab(true)
    onBookTagClick?.(type, id)
  }

  const handleModuleTagClick = (type: 'grade' | 'curriculum' | 'contributor', id: string) => {
    if (type === 'grade') form.setFieldValue('gradeId', [id])
    else if (type === 'curriculum') form.setFieldValue('curriculumId', [id])
    else if (type === 'contributor') form.setFieldValue('contributorId', [id])
    setSelectedModuleId(null)
    setActiveTab('practiceModules')
    setDidPickTab(true)
  }

  const renderBooks = (items: BookItem[]) => (
    <div className='srd:grid srd:grid-cols-2 srd:sm:grid-cols-3 srd:gap-4 srd:md:grid-cols-4 srd:lg:grid-cols-5 srd:xl:grid-cols-6 srd:2xl:grid-cols-8 srd:3xl:grid-cols-10 srd:4xl:grid-cols-12'>
      {items.map((book) => (
        <BookCoverCard
          key={book.id}
          id={book.id}
          coverUrl={book.coverUrl}
          action={{ type: 'callback', onClick: () => setSelectedBookId(book.id) }}
          className='srd:sm:w-full srd:aspect-3/4 srd:sm:h-auto srd:mx-auto'
        />
      ))}
    </div>
  )

  const renderCollections = (items: CollectionItem[]) => (
    <div className='srd:grid srd:grid-cols-1 srd:sm:grid-cols-2 srd:gap-4 srd:md:grid-cols-3 srd:lg:grid-cols-4 srd:xl:grid-cols-5 srd:2xl:grid-cols-6 srd:3xl:grid-cols-8 srd:4xl:grid-cols-10'>
      {items.map((collection) => (
        <BookCollectionCard
          key={collection.id}
          id={collection.id}
          coverUrl={collection.coverUrl}
          action={{
            type: 'link',
            renderWrapper: (content) =>
              renderBookCollectionLink({
                meta: { bookCollectionId: collection.id, bookCollectionSlug: collection.slug },
                className: 'srd:block',
                children: content,
              }),
          }}
          className='srd:sm:w-full srd:aspect-4/3 srd:sm:h-auto srd:mx-auto'
        />
      ))}
    </div>
  )

  const renderModules = (items: ModuleItem[]) => (
    <div className='srd:grid srd:grid-cols-1 srd:sm:grid-cols-2 srd:gap-4 srd:md:grid-cols-3 srd:lg:grid-cols-4 srd:xl:grid-cols-5 srd:2xl:grid-cols-6 srd:3xl:grid-cols-8 srd:4xl:grid-cols-10'>
      {items.map((lessonModule) => (
        <LessonModuleCard
          key={lessonModule.id}
          id={lessonModule.id}
          coverUrl={lessonModule.coverUrl}
          action={{ type: 'callback', onClick: () => setSelectedModuleId(lessonModule.id) }}
          className='srd:sm:w-full srd:aspect-4/3 srd:sm:h-auto srd:mx-auto'
        />
      ))}
    </div>
  )

  const renderResultGroup = <T,>({
    query,
    renderItems,
    skeletonVariant,
  }: {
    query: {
      isPending: boolean
      fetchStatus: 'fetching' | 'paused' | 'idle'
      isError: boolean
      isFetchingNextPage: boolean
      hasNextPage: boolean
      data?: { pages: { items: T[] }[] }
      refetch: () => unknown
      fetchNextPage: () => unknown
    }
    renderItems: (items: T[]) => ReactNode
    skeletonVariant: 'book' | 'bookCollection' | 'lessonModule'
  }) => {
    const isActuallyLoading = query.isPending && query.fetchStatus !== 'idle'
    if (isActuallyLoading) {
      return (
        <div
          className={cn(
            skeletonVariant === 'book'
              ? 'srd:grid srd:grid-cols-2 srd:sm:grid-cols-3 srd:gap-4 srd:md:grid-cols-4 srd:lg:grid-cols-5 srd:xl:grid-cols-6 srd:2xl:grid-cols-8 srd:3xl:grid-cols-10 srd:4xl:grid-cols-12'
              : 'srd:grid srd:grid-cols-1 srd:sm:grid-cols-2 srd:gap-4 srd:md:grid-cols-3 srd:lg:grid-cols-4 srd:xl:grid-cols-5 srd:2xl:grid-cols-6 srd:3xl:grid-cols-8 srd:4xl:grid-cols-10',
          )}
        >
          {Array.from({ length: 12 }).map((_, index) => (
            <ResultSkeleton key={`search-skeleton-${index}`} variant={skeletonVariant} />
          ))}
        </div>
      )
    }

    if (query.isError) return <ErrorState onRetry={() => void query.refetch()} />

    const items = (query.data?.pages.flatMap((page) => page.items) ?? []) as T[]
    if (items.length === 0) return <EmptyState />

    return (
      <section className='srd:flex srd:flex-col srd:gap-y-6'>
        {renderItems(items)}

        {query.isFetchingNextPage && (
          <div
            className={cn(
              skeletonVariant === 'book'
                ? 'srd:grid srd:grid-cols-2 srd:sm:grid-cols-3 srd:gap-4 srd:md:grid-cols-4 srd:lg:grid-cols-5 srd:xl:grid-cols-6 srd:2xl:grid-cols-8 srd:3xl:grid-cols-10 srd:4xl:grid-cols-12'
                : 'srd:grid srd:grid-cols-1 srd:sm:grid-cols-2 srd:gap-4 srd:md:grid-cols-3 srd:lg:grid-cols-4 srd:xl:grid-cols-5 srd:2xl:grid-cols-6 srd:3xl:grid-cols-8 srd:4xl:grid-cols-10',
            )}
          >
            {Array.from({ length: skeletonVariant === 'book' ? 12 : 10 }).map((_, index) => (
              <ResultSkeleton key={`search-more-skeleton-${index}`} variant={skeletonVariant} />
            ))}
          </div>
        )}

        {query.hasNextPage && (
          <div className='srd:flex srd:justify-center'>
            <Button
              type='button'
              variant='secondary'
              loading={query.isFetchingNextPage}
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              Lebih banyak <ChevronDown className='srd:size-4' />
            </Button>
          </div>
        )}
      </section>
    )
  }

  const renderActiveResults = () => {
    if (activeTab === 'books') {
      if (!booksQueryEnabled) return <EmptyState />
      return renderResultGroup<BookItem>({ query: booksQuery, renderItems: renderBooks, skeletonVariant: 'book' })
    }
    if (activeTab === 'collections') {
      if (!collectionsQueryEnabled) return <EmptyState />
      return renderResultGroup<CollectionItem>({
        query: collectionsQuery,
        renderItems: renderCollections,
        skeletonVariant: 'bookCollection',
      })
    }
    if (activeTab === 'practiceModules') {
      if (!lessonsQueryEnabled) return <EmptyState />
      return renderResultGroup<ModuleItem>({
        query: practiceModulesQuery,
        renderItems: renderModules,
        skeletonVariant: 'lessonModule',
      })
    }
    if (!lessonsQueryEnabled) return <EmptyState />
    return renderResultGroup<ModuleItem>({
      query: assignmentModulesQuery,
      renderItems: renderModules,
      skeletonVariant: 'lessonModule',
    })
  }

  return (
    <main className={cn('srd:flex srd:flex-col', className)}>
      <div className='srd:w-full srd:border-b srd:border-outline-variant/50 srd:px-4 srd:py-6'>
        <div className='srd:flex srd:w-full srd:items-center srd:gap-3 srd:mx-auto srd:max-w-[1200px]'>
          <Button type='button' asChild variant='ghost' size='icon' className='srd:rounded-full srd:text-neutral-white'>
            {renderBackLink({ children: <ArrowLeft className='srd:size-6' /> })}
          </Button>

          <ContentSearch
            value={formValues.q ?? ''}
            onChange={(val) => form.setFieldValue('q', val)}
            onSubmit={(val) => {
              form.setFieldValue('q', val)
              createUnifiedRecentSearch({ searchTerm: val })
            }}
            placeholder='Cari buku atau tugas'
            defaultOpen
            className='srd:flex-1'
            variant='inline'
          />
        </div>
      </div>

      {isDefaultView ? (
        <section className='srd:px-4 srd:py-8 srd:mx-auto srd:w-full srd:max-w-[1200px] srd:flex srd:flex-col srd:gap-y-10'>
          <div className='srd:flex srd:flex-col srd:gap-y-4'>
            <h2 className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-white srd:text-center')}>
              Genre Buku
            </h2>
            {bookThemesQuery.isPending ? (
              <div className='srd:flex srd:flex-wrap srd:gap-3 srd:items-center srd:justify-center'>
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className='srd:size-[104px] srd:rounded-2xl' />
                ))}
              </div>
            ) : (
              <div className='srd:flex srd:flex-wrap srd:gap-3 srd:items-center srd:justify-center'>
                {(bookThemesQuery.data ?? []).map((theme) => (
                  <BookThemeCard
                    key={theme.id}
                    id={theme.id}
                    name={theme.name}
                    iconUrl={theme.iconUrl}
                    size='large'
                    action={{
                      type: 'callback',
                      onClick: () => {
                        form.setFieldValue('bookThemeId', [theme.id])
                        setDidPickTab(true)
                        setActiveTab('books')
                      },
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          <div className='srd:flex srd:flex-col srd:gap-y-4'>
            <h2 className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-white srd:text-center')}>
              Kurikulum
            </h2>
            {curriculaQuery.isPending ? (
              <div className='srd:flex srd:flex-wrap srd:gap-3 srd:items-center srd:justify-center'>
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className='srd:size-[104px] srd:rounded-2xl' />
                ))}
              </div>
            ) : (
              <div className='srd:flex srd:flex-wrap srd:gap-3 srd:items-center srd:justify-center'>
                {(curriculaQuery.data ?? []).map((curriculum) => (
                  <BookThemeCard
                    key={curriculum.id}
                    id={curriculum.id}
                    name={curriculum.name}
                    iconUrl={curriculum.iconUrl ?? undefined}
                    size='large'
                    action={{
                      type: 'callback',
                      onClick: () => {
                        form.setFieldValue('curriculumId', [curriculum.id])
                        setDidPickTab(true)
                        setActiveTab('practiceModules')
                      },
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          <div className='srd:flex srd:flex-col srd:gap-y-4'>
            <h2 className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-white srd:text-center')}>
              Jenjang Baca
            </h2>
            {bookLevelsQuery.isPending ? (
              <div className='srd:flex srd:flex-wrap srd:gap-3 srd:mx-auto srd:items-center srd:justify-center'>
                {Array.from({ length: 7 }).map((_, i) => (
                  <Skeleton key={i} className='srd:h-10 srd:w-14 srd:rounded-full' />
                ))}
              </div>
            ) : (
              <div className='srd:flex srd:flex-wrap srd:gap-3 srd:mx-auto srd:items-center srd:justify-center'>
                {(bookLevelsQuery.data ?? []).map((level) => (
                  <button
                    key={level.id}
                    type='button'
                    onClick={() => {
                      form.setFieldValue('bookLevelId', [level.id])
                      setDidPickTab(true)
                      setActiveTab('books')
                    }}
                    className={cn(
                      typographyVariants({ variant: 'label-md' }),
                      'srd:border srd:border-blue-100 srd:px-4 srd:py-2 srd:rounded-full srd:text-neutral-white',
                    )}
                  >
                    {level.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className='srd:flex srd:flex-col srd:gap-y-4'>
            <h2 className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-white srd:text-center')}>
              Format Buku
            </h2>
            {bookFormatsQuery.isPending ? (
              <div className='srd:flex srd:flex-wrap srd:gap-3 srd:mx-auto srd:items-center srd:justify-center'>
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className='srd:h-10 srd:w-28 srd:rounded-full' />
                ))}
              </div>
            ) : (
              <div className='srd:flex srd:flex-wrap srd:gap-3 srd:mx-auto srd:items-center srd:justify-center'>
                {(bookFormatsQuery.data ?? []).map((format) => (
                  <button
                    key={format.id}
                    type='button'
                    onClick={() => {
                      form.setFieldValue('bookFormatId', [format.id])
                      setDidPickTab(true)
                      setActiveTab('books')
                    }}
                    className={cn(
                      typographyVariants({ variant: 'label-md' }),
                      'srd:border srd:border-blue-100 srd:px-4 srd:py-2 srd:rounded-full srd:text-neutral-white',
                    )}
                  >
                    {format.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className='srd:flex srd:flex-col srd:gap-y-4'>
            <h2 className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-white srd:text-center')}>
              Tingkatan
            </h2>
            {gradesQuery.isPending ? (
              <div className='srd:flex srd:flex-wrap srd:gap-3 srd:mx-auto srd:items-center srd:justify-center'>
                {Array.from({ length: 7 }).map((_, i) => (
                  <Skeleton key={i} className='srd:h-10 srd:w-20 srd:rounded-full' />
                ))}
              </div>
            ) : (
              <div className='srd:flex srd:flex-wrap srd:gap-3 srd:mx-auto srd:items-center srd:justify-center'>
                {(gradesQuery.data ?? []).map((grade) => (
                  <button
                    key={grade.id}
                    type='button'
                    onClick={() => {
                      form.setFieldValue('gradeId', [grade.id])
                      setDidPickTab(true)
                      setActiveTab('practiceModules')
                    }}
                    className={cn(
                      typographyVariants({ variant: 'label-md' }),
                      'srd:border srd:border-blue-100 srd:px-4 srd:py-2 srd:rounded-full srd:text-neutral-white',
                    )}
                  >
                    {grade.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      ) : (
        <>
          <div className='srd:px-4 srd:py-4 srd:w-full'>
            <div className='srd:mx-auto srd:flex srd:w-full srd:max-w-[1200px] srd:flex-col srd:gap-y-4'>
              <div className='srd:w-full srd:flex srd:items-center srd:justify-between srd:gap-4 srd:flex-wrap'>
                <p className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-white')}>
                  Hasil Pencarian
                </p>

                <Dialog>
                  <DialogTrigger
                    render={(props) => (
                      <Button {...props} type='button' variant='outline' className='srd:text-neutral-white'>
                        <Filter className='srd:size-5 srd:mr-2' />
                        Filter
                      </Button>
                    )}
                  />
                  <DialogContent className='srd:max-w-[640px]'>
                    <DialogHeader>
                      <DialogTitle>Filter pencarian</DialogTitle>
                      <DialogDescription>
                        Pilih filter yang ingin diterapkan. Pencarian untuk semua konten akan berdasarkan kata kunci dan
                        filter yang dipilih.
                      </DialogDescription>
                    </DialogHeader>

                    <div className='srd:grid srd:grid-cols-1 srd:gap-3 srd:md:grid-cols-2'>
                      <label className='srd:flex srd:flex-col srd:gap-1 srd:col-span-full'>
                        <span className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-navy-100')}>
                          Umur
                        </span>
                        <SliderRange
                          showThumbValueLabels
                          thumbValueLabelClassName='srd:text-neutral-black'
                          min={4}
                          max={12}
                          step={1}
                          value={
                            formValues.age && formValues.age.length === 2
                              ? [formValues.age[0] as number, formValues.age[1] as number]
                              : [4, 12]
                          }
                          onValueChange={([min, max]) => {
                            form.setFieldValue('age', min === 4 && max === 12 ? undefined : [min, max])
                          }}
                        />
                      </label>

                      <ComboboxFilter
                        label='Jenjang baca'
                        value={formValues.bookLevelId}
                        options={bookLevelsQuery.data ?? []}
                        onChange={(value) => form.setFieldValue('bookLevelId', value)}
                      />
                      <ComboboxFilter
                        label='Format buku'
                        value={formValues.bookFormatId}
                        options={bookFormatsQuery.data ?? []}
                        onChange={(value) => form.setFieldValue('bookFormatId', value)}
                      />
                      <ComboboxFilter
                        label='Genre buku'
                        value={formValues.bookThemeId}
                        options={bookThemesQuery.data ?? []}
                        onChange={(value) => form.setFieldValue('bookThemeId', value)}
                      />
                      <ComboboxFilter
                        label='Tingkat kelas'
                        value={formValues.gradeId}
                        options={gradesQuery.data ?? []}
                        onChange={(value) => form.setFieldValue('gradeId', value)}
                      />
                      <ComboboxFilter
                        label='Mata pelajaran'
                        value={formValues.subjectId}
                        options={subjectsQuery.data ?? []}
                        onChange={(value) => form.setFieldValue('subjectId', value)}
                      />
                      <ComboboxFilter
                        label='Kurikulum'
                        value={formValues.curriculumId}
                        options={curriculaQuery.data ?? []}
                        onChange={(value) => form.setFieldValue('curriculumId', value)}
                      />
                      <ComboboxFilter
                        label='Keterampilan'
                        value={formValues.competencyId}
                        options={competenciesQuery.data ?? []}
                        onChange={(value) => form.setFieldValue('competencyId', value)}
                      />
                    </div>

                    <DialogFooter>
                      <DialogClose render={<Button variant='outline'>Tutup</Button>} />
                      <DialogClose render={<Button>Perbarui filter</Button>} />
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>

          {(() => {
            type StringArrayField =
              | 'bookThemeId'
              | 'bookLevelId'
              | 'bookFormatId'
              | 'gradeId'
              | 'subjectId'
              | 'curriculumId'
              | 'competencyId'
            type ChipFieldKey = StringArrayField | 'publisherId' | 'contributorId'
            interface Chip {
              fieldKey: ChipFieldKey
              id: string
              label: string
              iconUrl?: string
            }
            const chips: Chip[] = []

            const pushChips = (
              fieldKey: StringArrayField,
              ids: string[] | undefined,
              options: { id: string; name: string; iconUrl?: string | null }[],
            ) => {
              if (!ids?.length) return
              for (const id of ids) {
                const opt = options.find((o) => o.id === id)
                if (!opt) continue
                const iconUrl = typeof opt.iconUrl === 'string' && opt.iconUrl.trim() !== '' ? opt.iconUrl : undefined
                chips.push({ fieldKey, id, label: opt.name, iconUrl })
              }
            }

            pushChips('bookThemeId', formValues.bookThemeId, bookThemesQuery.data ?? [])
            pushChips('bookLevelId', formValues.bookLevelId, bookLevelsQuery.data ?? [])
            pushChips('bookFormatId', formValues.bookFormatId, bookFormatsQuery.data ?? [])
            pushChips('gradeId', formValues.gradeId, gradesQuery.data ?? [])
            pushChips('subjectId', formValues.subjectId, subjectsQuery.data ?? [])
            pushChips('curriculumId', formValues.curriculumId, curriculaQuery.data ?? [])
            pushChips('competencyId', formValues.competencyId, competenciesQuery.data ?? [])

            for (const id of formValues.contributorId ?? []) {
              const meta = contributorMetaById.get(id)
              const avatar = meta?.avatarUrl
              const iconUrl = typeof avatar === 'string' && avatar.trim() !== '' ? avatar : undefined
              chips.push({
                fieldKey: 'contributorId',
                id,
                label: meta?.name ?? id,
                iconUrl,
              })
            }

            for (const id of formValues.publisherId ?? []) {
              const meta = publisherMetaById.get(id)
              const logo = meta?.logo
              const iconUrl = typeof logo === 'string' && logo.trim() !== '' ? logo : undefined
              chips.push({
                fieldKey: 'publisherId',
                id,
                label: meta?.name ?? id,
                iconUrl,
              })
            }

            const hasAge = formValues.age && formValues.age.length === 2

            return (
              <div className='srd:px-4 srd:pt-2 srd:pb-8 srd:border-b srd:border-outline-variant/50'>
                <div className='srd:mx-auto srd:flex srd:w-full srd:max-w-[1200px] srd:flex-wrap srd:gap-2'>
                  {/* Placeholder for border bottom */}
                  {chips.length === 0 && <div className='srd:w-1 srd:h-1' />}

                  {chips.map((chip) => (
                    <button
                      key={`${chip.fieldKey}-${chip.id}`}
                      type='button'
                      onClick={() => {
                        if (chip.fieldKey === 'publisherId') {
                          const current = formValues.publisherId ?? []
                          form.setFieldValue(
                            'publisherId',
                            current.filter((pid) => pid !== chip.id),
                          )
                          return
                        }
                        if (chip.fieldKey === 'contributorId') {
                          const current = formValues.contributorId ?? []
                          form.setFieldValue(
                            'contributorId',
                            current.filter((cid) => cid !== chip.id),
                          )
                          return
                        }
                        const current = (formValues[chip.fieldKey] as string[] | undefined) ?? []
                        form.setFieldValue(
                          chip.fieldKey,
                          current.filter((id) => id !== chip.id),
                        )
                      }}
                      className='srd:flex srd:items-center srd:rounded-xl srd:bg-blue-30 srd:px-4 srd:py-2 srd:text-navy-80 srd:hover:bg-blue-40'
                    >
                      {chip.iconUrl ? (
                        <img
                          src={chip.iconUrl}
                          alt=''
                          className='srd:size-6 srd:shrink-0 srd:rounded-full srd:object-cover srd:mr-1.5'
                          draggable={false}
                        />
                      ) : null}
                      <span className={typographyVariants({ variant: 'label-md' })}>{chip.label}</span>
                      <X className='srd:size-4 srd:shrink-0 srd:ml-3 srd:hover:text-navy-100' />
                    </button>
                  ))}
                  {hasAge && (
                    <button
                      type='button'
                      onClick={() => form.setFieldValue('age', undefined)}
                      className='srd:flex srd:items-center srd:rounded-xl srd:bg-blue-30 srd:px-4 srd:py-2 srd:text-navy-80 srd:hover:bg-blue-40'
                    >
                      <span className={typographyVariants({ variant: 'label-md' })}>
                        {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                        {String(formValues.age![0])} - {String(formValues.age![1])} tahun
                      </span>
                      <X className='srd:size-4 srd:shrink-0 srd:ml-3 srd:hover:text-navy-100' />
                    </button>
                  )}
                </div>
              </div>
            )
          })()}

          <section className='srd:flex srd:w-full srd:flex-col srd:gap-y-4 srd:pt-4'>
            <Tabs
              value={activeTab}
              onValueChange={(value) => {
                const tab = value as SearchTab
                setActiveTab(tab)
                setDidPickTab(true)
                onTabChange?.(tab)
              }}
              className='srd:gap-y-4'
            >
              <div className='srd:flex srd:w-full srd:flex-col srd:gap-y-3'>
                <TabsList
                  variant='line'
                  className='srd:flex srd:h-auto srd:w-full srd:max-w-none srd:justify-stretch ui:w-full'
                >
                  {visibleTabs.map((tab) => (
                    <TabsTrigger
                      key={tab.id}
                      value={tab.id}
                      className='srd:min-w-0 srd:flex-1 srd:gap-2 srd:px-3 srd:after:bg-outline-variant/50 ui:group-data-[variant=line]/tabs-list:text-grey-70 ui:group-data-[variant=line]/tabs-list:data-active:text-navy-50 srd:h-[47px] srd:flex srd:items-center srd:justify-center ui:group-data-[variant=line]/tabs-list:hover:text-grey-60'
                    >
                      <span className='srd:inline-flex srd:min-w-0 srd:items-center srd:gap-2'>
                        <tab.Icon size={24} color='currentColor' className='srd:shrink-0 srd:size-6' aria-hidden />
                        <span className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:truncate')}>
                          ({tabBadgeText[tab.id]})
                        </span>
                      </span>
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              {visibleTabs.map((tab) => (
                <TabsContent key={tab.id} value={tab.id}>
                  <div className='srd:mx-auto srd:flex srd:w-full srd:flex-col srd:gap-6 srd:px-4 srd:pt-6'>
                    <h2 className={cn(typographyVariants({ variant: 'title-lg' }), 'srd:text-neutral-white')}>
                      {tab.heading}
                    </h2>
                    <AnimatePresence mode='wait'>
                      <motion.div
                        key={tab.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.18 }}
                      >
                        {renderActiveResults()}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </section>
        </>
      )}

      <BookDetailModal
        open={!!selectedBookId}
        onClose={() => setSelectedBookId(null)}
        book={bookDetails}
        isLoading={bookDetailsLoading && !!selectedBookId}
        isError={bookDetailsError}
        renderBookAction={renderBookModalAction}
        renderCollectionLink={renderCollectionLink}
        renderLoveBookLink={renderLoveBookLink}
        onTagClick={handleBookTagClick}
        withShareCta
      />

      <LessonModuleModal
        open={!!selectedModuleId}
        onClose={() => setSelectedModuleId(null)}
        data={mappedModuleData}
        isLoading={moduleDetailsLoading && !!selectedModuleId}
        isError={moduleDetailsError}
        renderActivityAction={mappedModuleData ? renderLessonModuleModalAction(mappedModuleData) : undefined}
        onTagClick={handleModuleTagClick}
        showLoveButton={showLoveButton}
      />
    </main>
  )
}
